/**
 * Análises (D522): what a refused or failed BI call is called on screen. The API answers with stable reason codes
 * (`services/vello-api/src/analytics-bi-routes.ts`); a screen shows plain words for each and never the code itself as the
 * message (the code is kept as a small reference for support). Pure: the copy ids are resolved by the caller.
 */
import { type ApiError } from '@vello/api-client';

export type BiErrorKind =
  | 'busy' // 429 ANALYTICS_BUSY — too many heavy reads in flight for this user; retry shortly
  | 'timeout' // 503 ANALYTICS_QUERY_TIMEOUT or a client timeout — narrow the period or the cut
  | 'unavailable' // 503 ANALYTICS_UNAVAILABLE / ACCESS_LOG_UNAVAILABLE, 404 (module not enabled here)
  | 'network'
  | 'forbidden' // bare 403 — the operator has no analytics word here
  | 'market_forbidden'
  | 'personal_forbidden'
  | 'linkage_forbidden' // distinct-people measures with entity-level cuts need the personal word
  | 'too_large' // 413 export over the row cap
  | 'period_too_long'
  | 'hour_range'
  | 'metrics_mix'
  | 'dimension_unavailable'
  | 'filter_value'
  | 'filter_shape' // D528: an operator the dimension does not take, too many conditions, a bad group, a missing currency
  | 'having' // D528: a HAVING condition the planner refuses
  | 'compare_unplaceable'
  | 'request';

export interface BiErrorView {
  readonly kind: BiErrorKind;
  /** The catalog id of the plain-language message. */
  readonly messageId: string;
  /** A retry is worth offering (the cause is transient). */
  readonly retryable: boolean;
  /** The server's stable code, kept as a small reference (never the message). */
  readonly reference: string;
  /** Seconds the server asked to wait before another try. */
  readonly retryAfterSeconds?: number;
}

const BY_REASON: Readonly<Record<string, BiErrorKind>> = {
  ANALYTICS_BUSY: 'busy',
  ANALYTICS_QUERY_TIMEOUT: 'timeout',
  ANALYTICS_UNAVAILABLE: 'unavailable',
  ANALYTICS_ACCESS_LOG_UNAVAILABLE: 'unavailable',
  ANALYTICS_MARKET_FORBIDDEN: 'market_forbidden',
  ANALYTICS_PERSONAL_FORBIDDEN: 'personal_forbidden',
  ANALYTICS_LINKAGE_FORBIDDEN: 'linkage_forbidden',
  ANALYTICS_EXPORT_TOO_LARGE: 'too_large',
  PERIOD_TOO_LONG: 'period_too_long',
  PERIOD_REVERSED: 'request',
  HOUR_GRAIN_RANGE_TOO_LONG: 'hour_range',
  METRICS_SPAN_FACT_TABLES: 'metrics_mix',
  DIMENSION_NOT_AVAILABLE_FOR_METRICS: 'dimension_unavailable',
  DIMENSIONS_NOT_COMBINABLE: 'dimension_unavailable',
  FILTER_NOT_AVAILABLE_FOR_METRICS: 'dimension_unavailable',
  FILTER_VALUE_INVALID: 'filter_value',
  FILTER_OPERATOR_NOT_ALLOWED: 'filter_shape',
  FILTER_CURRENCY_REQUIRED: 'filter_shape',
  FILTER_CURRENCY_INVALID: 'filter_shape',
  FILTER_CURRENCY_NOT_ALLOWED: 'filter_shape',
  FILTER_TOO_MANY: 'filter_shape',
  FILTER_GROUP_INVALID: 'filter_shape',
  FILTER_DUPLICATED: 'filter_shape',
  FILTER_INVALID: 'filter_shape',
  FILTER_UNKNOWN_DIMENSION: 'filter_shape',
  DIMENSION_FILTER_ONLY: 'filter_shape',
  HAVING_INVALID: 'having',
  HAVING_VALUE_INVALID: 'having',
  HAVING_METRIC_NOT_SELECTED: 'having',
  HAVING_METRIC_UNKNOWN: 'having',
  HAVING_CURRENCY_REQUIRED: 'having',
  HAVING_CURRENCY_INVALID: 'having',
  HAVING_CURRENCY_NOT_ALLOWED: 'having',
  PERIOD_INVALID: 'request',
  COMPARE_PERIOD_UNPLACEABLE: 'compare_unplaceable',
  // D527: the pending reports
  REPORT_BUSY: 'busy',
  LIST_EXPORT_TOO_LARGE: 'too_large',
  ACCESS_EXPORT_TOO_LARGE: 'too_large',
  ACCESS_AUDIT_UNAVAILABLE: 'unavailable',
  LIST_RANGE_TOO_LONG: 'period_too_long',
  ACCESS_RANGE_TOO_LONG: 'period_too_long',
  LIST_FILTER_INVALID: 'filter_value',
  ACCESS_FILTER_INVALID: 'filter_value',
  LIST_MARKET_INVALID: 'filter_value',
  LIST_RANGE_INVALID: 'request',
  ACCESS_RANGE_INVALID: 'request',
};

const MESSAGE: Readonly<Record<BiErrorKind, string>> = {
  busy: 'bi.err.busy',
  timeout: 'bi.err.timeout',
  unavailable: 'bi.err.unavailable',
  network: 'bi.err.network',
  forbidden: 'bi.err.forbidden',
  market_forbidden: 'bi.err.marketForbidden',
  personal_forbidden: 'bi.err.personalForbidden',
  linkage_forbidden: 'bi.err.linkageForbidden',
  too_large: 'bi.err.tooLarge',
  period_too_long: 'bi.err.periodTooLong',
  hour_range: 'bi.err.hourRange',
  metrics_mix: 'bi.err.metricsMix',
  dimension_unavailable: 'bi.err.dimensionUnavailable',
  filter_value: 'bi.err.filterValue',
  filter_shape: 'bi.err.filterShape',
  having: 'bi.err.having',
  compare_unplaceable: 'bi.err.compareUnplaceable',
  request: 'bi.err.request',
};

const RETRYABLE: ReadonlySet<BiErrorKind> = new Set(['busy', 'timeout', 'unavailable', 'network']);

export function biErrorKind(error: Pick<ApiError, 'code' | 'status' | 'reasonCode'>): BiErrorKind {
  const reason = error.reasonCode;
  if (reason !== undefined && BY_REASON[reason] !== undefined) return BY_REASON[reason]!;
  if (error.status === 429 || error.code === 'RATE_LIMITED') return 'busy';
  if (error.status === 413) return 'too_large';
  if (error.code === 'NETWORK') return 'network';
  if (error.code === 'TIMEOUT') return 'timeout';
  if (error.status === 403 || error.code === 'FORBIDDEN') return 'forbidden';
  if (error.status === 404 || error.status === 503 || error.code === 'SERVICE_UNAVAILABLE') return 'unavailable';
  return 'request';
}

export function biErrorView(error: Pick<ApiError, 'code' | 'status' | 'reasonCode' | 'retryAfterSeconds'>): BiErrorView {
  const kind = biErrorKind(error);
  return {
    kind,
    messageId: MESSAGE[kind],
    retryable: RETRYABLE.has(kind),
    reference: error.reasonCode ?? error.code,
    ...(error.retryAfterSeconds !== undefined ? { retryAfterSeconds: error.retryAfterSeconds } : {}),
  };
}

/** The planner's refusal to place a year-before comparison (the automatic comparison then falls back to the previous period). */
export function isCompareUnplaceable(error: Pick<ApiError, 'reasonCode'>): boolean {
  return error.reasonCode === 'COMPARE_PERIOD_UNPLACEABLE';
}

// ───────────────────────────── saved reports (D523) ─────────────────────────────

const REPORT_MESSAGE: Readonly<Record<string, string>> = {
  REPORT_NAME_TAKEN: 'bi.reports.err.name_taken',
  REPORT_LIMIT_REACHED: 'bi.reports.err.limit',
  REPORT_NAME_INVALID: 'bi.reports.err.name_required',
  ANALYTICS_REPORT_NOT_FOUND: 'bi.reports.err.not_found',
  ANALYTICS_PERSONAL_FORBIDDEN: 'bi.reports.err.personal',
  REPORT_PERSONAL_VALUES_FORBIDDEN: 'bi.reports.err.pinned',
  REPORT_QUERY_TOO_LARGE: 'bi.reports.err.query',
  REPORT_QUERY_INVALID: 'bi.reports.err.query',
  REPORT_BODY_INVALID: 'bi.reports.err.query',
  ANALYTICS_REPORT_AUDIT_UNAVAILABLE: 'bi.reports.err.audit',
};

/**
 * What a refused or failed saved-report call is called on screen: the report's own reasons (a taken name, the limit, a person pinned in
 * a filter...) in their own words, a bare 403 as "your access does not keep reports", and anything else (network, timeout, a query the
 * planner refuses) the same plain words the rest of Análises uses.
 */
export function reportErrorView(error: Pick<ApiError, 'code' | 'status' | 'reasonCode' | 'retryAfterSeconds'>): BiErrorView {
  const own = error.reasonCode !== undefined ? REPORT_MESSAGE[error.reasonCode] : undefined;
  if (own !== undefined) return { kind: 'request', messageId: own, retryable: error.reasonCode === 'ANALYTICS_REPORT_AUDIT_UNAVAILABLE', reference: error.reasonCode ?? error.code };
  if ((error.status === 403 || error.code === 'FORBIDDEN') && error.reasonCode === undefined) return { kind: 'forbidden', messageId: 'bi.reports.err.forbidden', retryable: false, reference: error.code };
  return biErrorView(error);
}

// ───────────────────────────── saved filter sets and shared links (D528) ─────────────────────────────

const FILTER_SET_MESSAGE: Readonly<Record<string, string>> = {
  FILTER_SET_NAME_TAKEN: 'bi.sets.err.name_taken',
  FILTER_SET_LIMIT_REACHED: 'bi.sets.err.limit',
  FILTER_SET_NAME_INVALID: 'bi.sets.err.name_required',
  ANALYTICS_FILTER_SET_NOT_FOUND: 'bi.sets.err.not_found',
  FILTER_SET_PERSONAL_VALUES_FORBIDDEN: 'bi.sets.err.pinned',
  FILTER_SET_BODY_INVALID: 'bi.sets.err.state',
  FILTER_STATE_INVALID: 'bi.sets.err.state',
  FILTER_STATE_TOO_LARGE: 'bi.sets.err.state',
  FILTER_SET_STATE_INVALID: 'bi.sets.err.state',
  FILTER_SET_TOO_MANY_MARKETS: 'bi.sets.err.state',
  FILTER_SET_SHARE_NEEDS_MARKET: 'bi.sets.err.share_market',
  ANALYTICS_FILTER_SET_AUDIT_UNAVAILABLE: 'bi.sets.err.audit',
};

/** What a refused or failed filter-set call is called on screen (the set's own reasons in their own words; the rest as everywhere in Análises). */
export function filterSetErrorView(error: Pick<ApiError, 'code' | 'status' | 'reasonCode' | 'retryAfterSeconds'>): BiErrorView {
  const own = error.reasonCode !== undefined ? FILTER_SET_MESSAGE[error.reasonCode] : undefined;
  if (own !== undefined) return { kind: 'request', messageId: own, retryable: error.reasonCode === 'ANALYTICS_FILTER_SET_AUDIT_UNAVAILABLE', reference: error.reasonCode ?? error.code };
  if ((error.status === 403 || error.code === 'FORBIDDEN') && error.reasonCode === undefined) return { kind: 'forbidden', messageId: 'bi.sets.err.forbidden', retryable: false, reference: error.code };
  return biErrorView(error);
}

// ───────────────────────────── the goals (metas) of the scorecards (D531) ─────────────────────────────

const TARGET_MESSAGE: Readonly<Record<string, string>> = {
  TARGET_VALUE_INVALID: 'bi.targets.err.value',
  TARGET_BAND_INVALID: 'bi.targets.err.band',
  TARGET_METRIC_UNKNOWN: 'bi.targets.err.metric',
  TARGET_METRIC_UNIT_UNSUPPORTED: 'bi.targets.err.metric',
  TARGET_MARKET_INVALID: 'bi.targets.err.market',
  TARGET_BODY_INVALID: 'bi.targets.err.value',
  ANALYTICS_MARKET_FORBIDDEN: 'bi.targets.err.market_forbidden',
  ANALYTICS_TARGET_NOT_FOUND: 'bi.targets.err.not_found',
  ANALYTICS_TARGET_AUDIT_UNAVAILABLE: 'bi.targets.err.audit',
};

/** What a refused or failed goal call is called on screen. */
export function targetErrorView(error: Pick<ApiError, 'code' | 'status' | 'reasonCode' | 'retryAfterSeconds'>): BiErrorView {
  const own = error.reasonCode !== undefined ? TARGET_MESSAGE[error.reasonCode] : undefined;
  if (own !== undefined) return { kind: 'request', messageId: own, retryable: error.reasonCode === 'ANALYTICS_TARGET_AUDIT_UNAVAILABLE', reference: error.reasonCode ?? error.code };
  if ((error.status === 403 || error.code === 'FORBIDDEN') && error.reasonCode === undefined) return { kind: 'forbidden', messageId: 'bi.targets.err.forbidden', retryable: false, reference: error.code };
  return biErrorView(error);
}
